import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  interestClusterHint,
  interestClusterSubjectFamily,
  interestClusterTitle,
  interestMatchTier,
  pickInterestTileCount,
  relativeMatchPct,
  topClusterScore
} from '../../utils/interestClusterDisplay';
import { getInterestClusterTileStyle } from '../../utils/interestTheme';
import InterestClusterInfoModal from './InterestClusterInfoModal';

const TIER_LABEL_KEYS = {
  best: 'interestCluster.tierBest',
  strong: 'interestCluster.tierStrong',
  good: 'interestCluster.tierGood',
  explore: 'interestCluster.tierExplore'
};

const TILE_HOVER =
  'transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-lg hover:shadow-black/10 dark:hover:shadow-black/40 motion-reduce:transition-none motion-reduce:hover:translate-y-0';

const COMPACT_DETAILS_LABEL_MAX_WIDTH = 260;

function InterestTile({
  cluster,
  rank,
  topScore,
  className = '',
  featured = false,
  interactive = true,
  onOpen,
  revealSequence = false,
  revealIndex = 0,
  rowLayout = false
}) {
  const { t } = useTranslation();
  const tileRef = useRef(null);
  const [compactDetailsLabel, setCompactDetailsLabel] = useState(false);
  const family = interestClusterSubjectFamily(cluster);
  const style = getInterestClusterTileStyle(cluster, family);
  const title = interestClusterTitle(cluster, t);
  const hint = interestClusterHint(cluster, t);
  const relativePct = relativeMatchPct(cluster.score, topScore);
  const tier = interestMatchTier(relativePct, rank);
  const tierLabel = t(TIER_LABEL_KEYS[tier]);

  useEffect(() => {
    const node = tileRef.current;
    if (!node) return undefined;

    const updateLabelMode = (width) => {
      setCompactDetailsLabel(width < COMPACT_DETAILS_LABEL_MAX_WIDTH);
    };

    updateLabelMode(node.getBoundingClientRect().width);

    const observer = new ResizeObserver(([entry]) => {
      updateLabelMode(entry.contentRect.width);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const inner = (
    <>
      <div>
        <div className="flex items-center justify-between gap-3">
          <span
            className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold leading-none transition-colors duration-200 group-hover:brightness-110 ${style.badge}`}
          >
            {t('interestCluster.rankLabel', { rank })}
          </span>
          {interactive ? (
            <span className={`inline-flex shrink-0 items-center rounded-full border border-edge/80 bg-surface/80 px-2.5 py-0.5 text-xs font-semibold leading-none shadow-sm ${style.hint}`}>
              {compactDetailsLabel
                ? t('interestCluster.clickForDetailsShort')
                : t('interestCluster.clickForDetails')}
            </span>
          ) : null}
        </div>
        <h4
          className={`mt-3 font-semibold leading-snug transition-transform duration-200 group-hover:translate-x-0.5 ${
            featured ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg'
          }`}
        >
          {title}
        </h4>
        {hint ? (
          <p className={`mt-2 text-xs sm:text-sm leading-relaxed ${style.hint}`}>{hint}</p>
        ) : null}
      </div>
      <p
        className={`mt-4 font-bold leading-tight transition-opacity duration-200 group-hover:opacity-100 ${
          rowLayout ? 'sm:ml-6 sm:mt-0 sm:shrink-0 sm:text-right' : ''
        } ${
          featured ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl opacity-95'
        }`}
      >
        {tierLabel}
      </p>
    </>
  );

  const tileClass = `group rounded-2xl border p-4 sm:p-5 flex flex-col justify-between ${TILE_HOVER} ${
    featured ? 'shadow-md hover:shadow-xl' : 'shadow-sm'
  } ${rowLayout ? 'sm:flex-row sm:items-center' : ''} ${style.card} ${style.hover} ${
    revealSequence ? 'interest-result-tile-reveal' : ''
  } ${className}`;
  const revealStyle = revealSequence
    ? { animationDelay: `${revealIndex * 700}ms` }
    : undefined;

  if (!interactive) {
    return (
      <article ref={tileRef} className={`${tileClass} cursor-default`} style={revealStyle}>
        {inner}
      </article>
    );
  }

  return (
    <button
      ref={tileRef}
      type="button"
      onClick={() => onOpen?.(cluster, rank)}
      className={`${tileClass} cursor-pointer text-left w-full appearance-none ${TILE_HOVER}`}
      style={revealStyle}
      aria-label={t('interestCluster.popup.openTile', { title })}
    >
      {inner}
    </button>
  );
}

/**
 * User-friendly tile layout for top 3–5 interest clusters.
 * @param {{ clusters: Array<{cluster_id, theme, label, score}>, showCaption?: boolean, tileCount?: number|null }} props
 */
export default function InterestClusterTileGrid({
  clusters,
  showCaption = true,
  interactive = true,
  revealSequence = false,
  fullWidthRows = false,
  /** When set, skip gap-based 3–5 picking and show exactly this many tiles (capped by data). */
  tileCount = null
}) {
  const { t } = useTranslation();
  const [modalCluster, setModalCluster] = useState(null);
  const [modalRank, setModalRank] = useState(1);
  const sorted = Array.isArray(clusters) ? clusters : [];
  const count = tileCount != null
    ? Math.max(0, Math.min(Number(tileCount) || 0, sorted.length))
    : pickInterestTileCount(sorted);
  const top = sorted.slice(0, count);

  if (!top.length) return null;

  const topScore = topClusterScore(sorted);
  const layoutKey = count;
  const openCluster = (cluster, rank) => {
    setModalCluster(cluster);
    setModalRank(rank);
  };
  const tileProps = { topScore, interactive, onOpen: openCluster, revealSequence };

  return (
    <div>
      <div className="interest-tile-theme-purge-guard" aria-hidden />
      <InterestClusterInfoModal
        cluster={modalCluster}
        rank={modalRank}
        topScore={topScore}
        isOpen={Boolean(modalCluster)}
        onClose={() => setModalCluster(null)}
      />
      {showCaption ? (
        <p className="text-xs text-ink-subtle mb-4">{t('interestCluster.tileCaption')}</p>
      ) : null}

      {fullWidthRows ? (
        <div className="space-y-3">
          {top.map((cluster, index) => (
            <InterestTile
              key={cluster.cluster_id ?? index}
              cluster={cluster}
              rank={index + 1}
              revealIndex={index}
              rowLayout
              featured={index === 0}
              className="min-h-[126px] w-full"
              {...tileProps}
            />
          ))}
        </div>
      ) : null}

      {!fullWidthRows && layoutKey === 1 && (
        <InterestTile cluster={top[0]} rank={1} revealIndex={0} {...tileProps} featured className="min-h-[180px]" />
      )}

      {!fullWidthRows && layoutKey === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InterestTile cluster={top[0]} rank={1} revealIndex={0} {...tileProps} featured className="min-h-[200px]" />
          <InterestTile cluster={top[1]} rank={2} revealIndex={1} {...tileProps} className="min-h-[200px]" />
        </div>
      )}

      {!fullWidthRows && layoutKey === 3 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InterestTile cluster={top[0]} rank={1} revealIndex={0} {...tileProps} featured className="sm:col-span-2 min-h-[190px]" />
          <InterestTile cluster={top[1]} rank={2} revealIndex={1} {...tileProps} className="min-h-[160px]" />
          <InterestTile cluster={top[2]} rank={3} revealIndex={2} {...tileProps} className="min-h-[160px]" />
        </div>
      )}

      {!fullWidthRows && layoutKey === 4 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InterestTile cluster={top[0]} rank={1} revealIndex={0} {...tileProps} featured className="sm:row-span-2 min-h-[220px]" />
          <InterestTile cluster={top[1]} rank={2} revealIndex={1} {...tileProps} className="min-h-[104px]" />
          <InterestTile cluster={top[2]} rank={3} revealIndex={2} {...tileProps} className="min-h-[104px]" />
          <InterestTile cluster={top[3]} rank={4} revealIndex={3} {...tileProps} className="sm:col-span-2 min-h-[130px]" />
        </div>
      )}

      {!fullWidthRows && layoutKey === 5 && (
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <InterestTile cluster={top[0]} rank={1} revealIndex={0} {...tileProps} featured className="sm:col-span-4 min-h-[200px]" />
          <InterestTile cluster={top[1]} rank={2} revealIndex={1} {...tileProps} className="sm:col-span-2 min-h-[200px]" />
          <InterestTile cluster={top[2]} rank={3} revealIndex={2} {...tileProps} className="sm:col-span-2 min-h-[130px]" />
          <InterestTile cluster={top[3]} rank={4} revealIndex={3} {...tileProps} className="sm:col-span-2 min-h-[130px]" />
          <InterestTile cluster={top[4]} rank={5} revealIndex={4} {...tileProps} className="sm:col-span-2 min-h-[130px]" />
        </div>
      )}
    </div>
  );
}
